import React, { useState, useEffect, useRef } from "react";
import SearchIcon from "@mui/icons-material/Search";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import CloseIcon from "@mui/icons-material/Close";
import { Dropdown, Form } from "react-bootstrap";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { ReactTyped } from "react-typed";
import { getCodieSearchResults } from "../../api/Service";
import { toast } from "react-toastify";

export default function CodeSearchRecomendation() {
  const [show, setShow] = useState(false);

  // Filters
  const [selectedItems, setSelectedItems] = useState([]);

  // Search + results
  const [searchDone, setSearchDone] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);

  // Page results only
  const [filteredQueries, setFilteredQueries] = useState([]);

  const [selectedQueryIndex, setSelectedQueryIndex] = useState(null);
  const [selectedQuery, setSelectedQuery] = useState({});

  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const dropdownRef = useRef(null);

  const allItems = [
    "National Coding Advice",
    "New South Wales",
    "Queensland",
    "Victoria",
    "Western Australia",
  ];

  const stateChildren = [
    "New South Wales",
    "Queensland",
    "Victoria",
    "Western Australia",
  ];

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShow(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const isSelected = (item) => selectedItems.includes(item);
  const areAllSelected = allItems.every((i) => selectedItems.includes(i));
  const isStateParentActive = stateChildren.some((c) =>
    selectedItems.includes(c)
  );

  const handleItemChange = (item) => {
    let updated = [...selectedItems];

    if (item === "selectAll") {
      updated = areAllSelected ? [] : [...allItems];
    } else if (allItems.includes(item)) {
      updated = isSelected(item)
        ? updated.filter((i) => i !== item)
        : [...updated, item];
    }

    setSelectedItems(updated);
  };

  // SEARCH
  const handleSearch = async (newPage = 1) => {
    if (!searchQuery.trim()) {
      toast.error("Search term should not be empty");
      return;
    }

    setSearchDone(true);
    setLoading(true);

    const payload = {
      search_term: searchQuery,
      filters: selectedItems,
      page: newPage,
      page_size: 10,
    };

    const resp = await getCodieSearchResults(payload);

    setFilteredQueries(resp?.results || []);
    setPage(resp?.page || 1);
    setTotalPages(resp?.total_pages || 1);
    setSelectedQueryIndex(null);
    setLoading(false);
  };

  const handlePageChange = (nextPage) => {
    if (nextPage < 1 || nextPage > totalPages) return;
    handleSearch(nextPage);
  };

  // FETCH DETAIL
  const getMoreDetails = async (index) => {
    const item = filteredQueries[index];
    if (!item) return;

    setSelectedQueryIndex(index);
    setLoading(true);

    const payload = {
      action: "detail",
      query_number: item.query_number,
      reference: item.reference,
    };

    const resp = await getCodieSearchResults(payload);
    setSelectedQuery(resp || {});
    setLoading(false);
  };

  // NEXT
  const handleNext = async () => {
    if (selectedQueryIndex === null) return;

    // normal
    if (selectedQueryIndex < filteredQueries.length - 1) {
      getMoreDetails(selectedQueryIndex + 1);
      return;
    }

    // load next page
    if (page < totalPages) {
      const nextPage = page + 1;
      setLoading(true);

      const payload = {
        search_term: searchQuery,
        filters: selectedItems,
        page: nextPage,
        page_size: 10,
      };

      const resp = await getCodieSearchResults(payload);
      const results = resp?.results || [];

      setFilteredQueries(results);
      setPage(nextPage);

      if (results.length > 0) {
        setSelectedQueryIndex(0);

        const detailPayload = {
          action: "detail",
          query_number: results[0].query_number,
          reference: results[0].reference,
        };

        const detailResp = await getCodieSearchResults(detailPayload);
        setSelectedQuery(detailResp || {});
      }

      setLoading(false);
    }
  };

  // PREVIOUS
  const handlePrevious = async () => {
    if (selectedQueryIndex === null) return;

    if (selectedQueryIndex > 0) {
      getMoreDetails(selectedQueryIndex - 1);
      return;
    }

    // load previous page
    if (page > 1) {
      const prevPage = page - 1;
      setLoading(true);

      const payload = {
        search_term: searchQuery,
        filters: selectedItems,
        page: prevPage,
        page_size: 10,
      };

      const resp = await getCodieSearchResults(payload);

      const results = resp?.results || [];
      setFilteredQueries(results);
      setPage(prevPage);

      const lastIndex = results.length - 1;
      setSelectedQueryIndex(lastIndex);

      if (results.length > 0) {
        const last = results[lastIndex];

        const detailPayload = {
          action: "detail",
          query_number: last.query_number,
          reference: last.reference,
        };

        const detailResp = await getCodieSearchResults(detailPayload);
        setSelectedQuery(detailResp || {});
      }

      setLoading(false);
    }
  };

  const handleCloseDetail = () => setSelectedQueryIndex(null);

  // Debounced search update
useEffect(() => {
    if (!searchDone) return;
    if (!searchQuery.trim()) return;

    const id = setTimeout(() => handleSearch(1), 500);
    return () => clearTimeout(id);
}, [searchQuery, selectedItems]);

  // Auto-scroll
  useEffect(() => {
    const div = document.querySelector(".mainContent");
    div?.scrollTo({ top: 0, behavior: "smooth" });
  }, [selectedQuery]);

  useEffect(() => {
    return () => {
      toast.dismiss();
    };
  }, []);

  return (
    <div style={{ marginTop: 20, height: "95%" }}>
      <div style={{ fontSize: 30, marginLeft: 20 }}>
        {searchDone && (
          <div style={{ fontSize: 20 }}>
            Advice Search{" "}
            <SearchIcon sx={{ color: "#b6d7ea", fontSize: 30 }} />
          </div>
        )}
      </div>

      {/* SEARCH + FILTERS */}
      <div
        style={{
          margin: "10px 20px",
          width: "98%",
          display: "flex",
          justifyContent: searchDone ? "start" : "end",
        }}
      >
        {searchDone && (
          <div
            style={{
              width: "90%",
              height: 45,
              border: "1px solid #e0e0e0",
              borderRadius: 10,
              display: "flex",
              alignItems: "center",
              padding: "0 12px",
            }}
          >
            <input
              type="text"
              placeholder="🔍 Search for a condition (e.g., Cirrhosis)"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearch(1)}
              style={{ border: "none", outline: "none", width: "100%" }}
            />
            <SearchIcon
              onClick={() => handleSearch(1)}
              sx={{ cursor: "pointer", fontSize: 26 }}
            />
          </div>
        )}

        {/* FILTER DROPDOWN */}
        <div ref={dropdownRef} style={{ marginLeft: 10 }}>
          <Dropdown show={show} onToggle={setShow}>
            <Dropdown.Toggle
              variant="outline-primary"
              style={{
                borderRadius: 8,
                background: "transparent",
                borderColor: "#a7caff",
                color: "black",
                boxShadow: "none",
              }}
            >
              Active Search
            </Dropdown.Toggle>

            <Dropdown.Menu style={{ padding: 10, minWidth: 260 }}>
              <Form.Check
                label="Select All"
                type="checkbox"
                checked={areAllSelected}
                onChange={() => handleItemChange("selectAll")}
              />

              <hr />

              <Form.Check
                label="National Coding Advice"
                type="checkbox"
                checked={isSelected("National Coding Advice")}
                onChange={() => handleItemChange("National Coding Advice")}
              />

              <div style={{ marginTop: 5 }}>
                <Form.Check
                  label="State Coding Advice"
                  checked={isStateParentActive}
                  disabled
                />

                <div style={{ marginLeft: 20 }}>
                  {stateChildren.map((s) => (
                    <Form.Check
                      key={s}
                      label={s}
                      type="checkbox"
                      checked={isSelected(s)}
                      onChange={() => handleItemChange(s)}
                    />
                  ))}
                </div>
              </div>
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </div>

      {/* ⭐⭐ SKELETON DURING SEARCH ⭐⭐ */}
      {loading && selectedQueryIndex === null && (
        <div
          style={{
            width: "95%",
            marginLeft: 20,
            marginTop: 10,
          }}
        >
          <Skeleton height={28} count={8} />
        </div>
      )}

      {/* MAIN CONTENT (hidden when skeleton is active) */}
      {(!loading || selectedQueryIndex !== null) && (
        <div
          className="mainContent"
          style={{
            display: "flex",
            alignItems: searchDone ? "flex-start" : "center",
            justifyContent: "center",
            height: "57%",
            width: "97%",
            marginLeft: 20,
          }}
        >
          {/* INITIAL SCREEN */}
          {!searchDone ? (
            <div
              style={{
                width: "100%",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 25,
              }}
            >
              <div style={{ fontSize: 25 }}>
                Advice Search{" "}
                <SearchIcon sx={{ color: "#b6d7ea" }} />
              </div>

              <div
                style={{
                  position: "relative",
                  width: "60%",
                  height: 48,
                  border: "1px solid #d0d7de",
                  borderRadius: 12,
                  padding: "0 14px",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSearch(1)}
                  style={{ border: "none", outline: "none", width: "100%" }}
                />

                {!searchQuery && (
                  <div
                    style={{
                      position: "absolute",
                      left: 20,
                      color: "#999",
                    }}
                  >
                    <ReactTyped
                      strings={[
                        "🔍 Search for a condition (e.g., Cirrhosis)",
                        "Try Hepatitis B or Liver failure",
                        "Type ICD-10 keywords...",
                        "Search VICC queries...",
                      ]}
                      typeSpeed={50}
                      backSpeed={30}
                      loop
                    />
                  </div>
                )}

                <SearchIcon
                  onClick={() => handleSearch(1)}
                  sx={{ cursor: "pointer", fontSize: 28 }}
                />
              </div>
            </div>
          ) : selectedQueryIndex !== null ? (
            // DETAIL VIEW
            <div
              style={{
                width: "100%",
                background: "white",
                borderRadius: 12,
                padding: 24,
                boxShadow: "0 1px 3px rgba(0,0,0,0.15)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: 10,
                }}
              >
                <h2 className="text-2xl font-semibold text-blue-700">
                  {selectedQuery.query_number} —{" "}
                  {selectedQuery.query_title}
                </h2>

                <div style={{ display: "flex", gap: 12 }}>
                  <ArrowBackIosIcon
                    onClick={handlePrevious}
                    sx={{
                      cursor: "pointer",
                      color: "#555",
                      "&:hover": { color: "#1e88e5" },
                    }}
                  />
                  <ArrowForwardIosIcon
                    onClick={handleNext}
                    sx={{
                      cursor: "pointer",
                      color: "#555",
                      "&:hover": { color: "#1e88e5" },
                    }}
                  />
                  <CloseIcon
                    onClick={handleCloseDetail}
                    sx={{
                      cursor: "pointer",
                      color: "#666",
                      "&:hover": { color: "#1e88e5" },
                    }}
                  />
                </div>
              </div>

              <p style={{ marginBottom: 10, color: "#666" }}>
                Reference: {selectedQuery.reference} • Date:{" "}
                {selectedQuery.publication_date}
              </p>

              <div
                style={{
                  background: "#f9fafb",
                  borderRadius: 8,
                  border: "1px solid #e5e7eb",
                  padding: 16,
                  marginBottom: 20,
                }}
              >
                <h3 className="font-bold mb-2">Query</h3>
                <p className="whitespace-pre-wrap">
                  {selectedQuery.query_text}
                </p>
              </div>

              <div
                style={{
                  background: "#eef7ff",
                  borderRadius: 8,
                  border: "1px solid #bfdbfe",
                  padding: 16,
                }}
              >
                <h3 className="font-bold mb-2">Response</h3>
                <p className="whitespace-pre-wrap">
                  {selectedQuery.response}
                </p>
              </div>
            </div>
          ) : filteredQueries.length > 0 ? (
            // TABLE VIEW
            <div
              style={{
                width: "100%",
                background: "white",
                borderRadius: 10,
                padding: 10,
              }}
            >
              <table className="min-w-full border-collapse">
                <thead className="bg-blue-100 text-blue-700">
                  <tr>
                    <th className="text-left py-3 px-4 border-b">
                      Reference
                    </th>
                    <th className="text-left py-3 px-4 border-b">
                      Query Number
                    </th>
                    <th className="text-left py-3 px-4 border-b">
                      Query Title
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredQueries.map((q, idx) => (
                    <tr
                      key={q.query_number}
                      className="hover:bg-blue-50 cursor-pointer"
                      onClick={() => getMoreDetails(idx)}
                    >
                      <td className="py-3 px-4 border-b">
                        {q.reference}
                      </td>
                      <td className="py-3 px-4 border-b">
                        {q.query_number}
                      </td>
                      <td className="py-3 px-4 border-b">
                        {q.query_title}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems:"center",
                  marginTop: 20,
                  gap: 10,
                }}
              >
                <button
                  onClick={() => handlePageChange(page - 1)}
                  disabled={page === 1}
                  style={{
                    padding: "8px 14px",
                    borderRadius: 6,
                    background: page === 1 ? "#ccc" : "#1e88e5",
                    color: "white",
                    border: "none",
                  }}
                >
                  Previous
                </button>

                <span>
                  Page {page} of {totalPages}
                </span>

                <button
                  onClick={() => handlePageChange(page + 1)}
                  disabled={page === totalPages}
                  style={{
                    padding: "8px 14px",
                    borderRadius: 6,
                    background:
                      page === totalPages ? "#ccc" : "#1e88e5",
                    color: "white",
                    border: "none",
                  }}
                >
                  Next
                </button>
              </div>
            </div>
          ) : (
            <div style={{ marginTop: 20 }}>No matching queries found.</div>
          )}
        </div>
      )}
    </div>
  );
}
